// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import { useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { loginRequest, apiScopes } from './msal-config';

export function useAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const account = accounts[0] ?? null;
  const isLoading = inProgress !== InteractionStatus.None;

  const login = useCallback(async () => {
    if (inProgress !== InteractionStatus.None) return;
    await instance.loginRedirect(loginRequest);
  }, [instance, inProgress]);

  const logout = useCallback(async () => {
    await instance.logoutRedirect({ postLogoutRedirectUri: '/' });
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!account) {
      throw new Error('No active account');
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...apiScopes,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ ...loginRequest, account });
        throw new Error('Redirecting to login');
      }
      throw error;
    }
  }, [instance, account]);

  return {
    isAuthenticated,
    isLoading,
    account,
    login,
    logout,
    getAccessToken,
  };
}
